/**
 * EditorStore: the single mutation choke point for level editing.
 * Every change is a Command (do/undo pair) pushed through execute(), which
 * powers undo/redo, dirty tracking, and autosave (docs/EDITOR_SPEC.md §2).
 * The store is DOM-free and fully unit-testable.
 */
import type { LevelData, LevelObject } from '../format/types';
import { CURRENT_FORMAT_VERSION } from '../format/types';

export interface Command {
  label: string;
  do(level: LevelData): void;
  undo(level: LevelData): void;
}

const UNDO_DEPTH = 500;

export function blankLevel(name: string): LevelData {
  const now = new Date().toISOString();
  return {
    formatVersion: CURRENT_FORMAT_VERSION,
    meta: { name, author: 'You', revision: 0, createdAt: now, updatedAt: now },
    song: { id: 'neon-circuit', bpm: 128, offsetMs: 0 },
    settings: {
      mode: 'normal',
      speed: 'one',
      startForm: 'runner',
      accentColor: '#22d3ee',
      hazardColor: '#f43f5e',
      backgroundColor: '#0b0620',
    },
    objects: [],
  };
}

export class EditorStore {
  level: LevelData;
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  /** Selected object ids. */
  selection = new Set<number>();
  dirty = false;
  private nextObjectId: number;
  onChange: (() => void) | null = null;

  constructor(level: LevelData) {
    this.level = level;
    this.nextObjectId = level.objects.reduce((m, o) => Math.max(m, o.id), 0) + 1;
  }

  allocateId(): number {
    return this.nextObjectId++;
  }

  object(id: number): LevelObject | undefined {
    return this.level.objects.find((o) => o.id === id);
  }

  selectedObjects(): LevelObject[] {
    return this.level.objects.filter((o) => this.selection.has(o.id));
  }

  execute(cmd: Command): void {
    cmd.do(this.level);
    this.undoStack.push(cmd);
    if (this.undoStack.length > UNDO_DEPTH) this.undoStack.shift();
    this.redoStack.length = 0;
    this.markDirty();
  }

  undo(): boolean {
    const cmd = this.undoStack.pop();
    if (!cmd) return false;
    cmd.undo(this.level);
    this.redoStack.push(cmd);
    this.pruneSelection();
    this.markDirty();
    return true;
  }

  redo(): boolean {
    const cmd = this.redoStack.pop();
    if (!cmd) return false;
    cmd.do(this.level);
    this.undoStack.push(cmd);
    this.pruneSelection();
    this.markDirty();
    return true;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undoLabel(): string | null {
    return this.undoStack[this.undoStack.length - 1]?.label ?? null;
  }

  markSaved(): void {
    this.dirty = false;
    this.onChange?.();
  }

  private markDirty(): void {
    this.dirty = true;
    this.onChange?.();
  }

  /** Drop selection entries whose objects no longer exist (after undo/redo). */
  private pruneSelection(): void {
    const ids = new Set(this.level.objects.map((o) => o.id));
    for (const id of [...this.selection]) {
      if (!ids.has(id)) this.selection.delete(id);
    }
  }

  setSelection(ids: Iterable<number>): void {
    this.selection = new Set(ids);
    this.onChange?.();
  }

  clearSelection(): void {
    if (this.selection.size === 0) return;
    this.selection.clear();
    this.onChange?.();
  }
}

// --- Commands ----------------------------------------------------------------

export function addObjects(objects: LevelObject[]): Command {
  const copies = objects.map((o) => structuredClone(o));
  return {
    label: copies.length === 1 ? `Place ${copies[0]?.type}` : `Place ${copies.length} objects`,
    do(level) {
      level.objects.push(...copies.map((o) => structuredClone(o)));
    },
    undo(level) {
      const ids = new Set(copies.map((o) => o.id));
      level.objects = level.objects.filter((o) => !ids.has(o.id));
    },
  };
}

export function deleteObjects(objects: LevelObject[]): Command {
  const copies = objects.map((o) => structuredClone(o));
  const ids = new Set(copies.map((o) => o.id));
  return {
    label: `Delete ${copies.length} object${copies.length === 1 ? '' : 's'}`,
    do(level) {
      level.objects = level.objects.filter((o) => !ids.has(o.id));
    },
    undo(level) {
      level.objects.push(...copies.map((o) => structuredClone(o)));
    },
  };
}

export function moveObjects(ids: number[], dx: number, dy: number): Command {
  const idSet = new Set(ids);
  return {
    label: `Move ${ids.length} object${ids.length === 1 ? '' : 's'}`,
    do(level) {
      for (const o of level.objects) {
        if (idSet.has(o.id)) {
          o.x += dx;
          o.y += dy;
        }
      }
    },
    undo(level) {
      for (const o of level.objects) {
        if (idSet.has(o.id)) {
          o.x -= dx;
          o.y -= dy;
        }
      }
    },
  };
}

/** Replace whole objects (property edits). Captures before/after snapshots. */
export function updateObjects(before: LevelObject[], after: LevelObject[]): Command {
  const b = before.map((o) => structuredClone(o));
  const a = after.map((o) => structuredClone(o));
  const apply = (level: LevelData, list: LevelObject[]): void => {
    for (const repl of list) {
      const idx = level.objects.findIndex((o) => o.id === repl.id);
      if (idx >= 0) level.objects[idx] = structuredClone(repl);
    }
  };
  return {
    label: `Edit ${a.length} object${a.length === 1 ? '' : 's'}`,
    do(level) {
      apply(level, a);
    },
    undo(level) {
      apply(level, b);
    },
  };
}

/** Edit level meta/settings/song. */
export function updateLevelFields(
  before: Pick<LevelData, 'meta' | 'settings' | 'song'>,
  after: Pick<LevelData, 'meta' | 'settings' | 'song'>,
): Command {
  const b = structuredClone(before);
  const a = structuredClone(after);
  return {
    label: 'Edit level settings',
    do(level) {
      level.meta = structuredClone(a.meta);
      level.settings = structuredClone(a.settings);
      level.song = structuredClone(a.song);
    },
    undo(level) {
      level.meta = structuredClone(b.meta);
      level.settings = structuredClone(b.settings);
      level.song = structuredClone(b.song);
    },
  };
}
